import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import axios from 'axios';
import './Admin.css';

const API = process.env.REACT_APP_API_URL || 'https://api.blood.msdiscoveries.com/api';
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const emptyForm = { name: '', phone: '', address: '', blood_group: '' };

const Admin = () => {
  const navigate = useNavigate();
  const [donors, setDonors] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, byGroup: [] });

  const [modal, setModal] = useState(null); // null | 'add' | 'edit' | 'delete'
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('donors');
  const [contacts, setContacts] = useState([]);

  const token = localStorage.getItem('bb_admin_token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const logout = () => {
    localStorage.removeItem('bb_admin_token');
    toast.success('Logged out.');
    navigate('/admin');
  };

  const fetchDonors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.set('search', search);
      if (filterGroup) params.set('blood_group', filterGroup);

      const { data } = await axios.get(`${API}/donors?${params}`, authHeaders);
      setDonors(data.donors);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (err) {
      if (err.response?.status === 401) { logout(); return; }
      toast.error('Failed to load donors.');
    } finally {
      setLoading(false);
    }
  }, [page, search, filterGroup]);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/stats`);
      setStats(data);
    } catch {}
  };

  const fetchContacts = async () => {
    try {
      const { data } = await axios.get(`${API}/contact`, authHeaders);
      setContacts(data);
    } catch {}
  };

  useEffect(() => { fetchDonors(); }, [fetchDonors]);
  useEffect(() => { fetchStats(); fetchContacts(); }, []);

  // Debounce search
  useEffect(() => {
    setPage(1);
  }, [search, filterGroup]);

  const openAdd = () => { setForm(emptyForm); setSelected(null); setModal('add'); };
  const openEdit = (d) => { setForm({ name: d.name, phone: d.phone, address: d.address, blood_group: d.blood_group }); setSelected(d); setModal('edit'); };
  const openDelete = (d) => { setSelected(d); setModal('delete'); };
  const closeModal = () => { setModal(null); setSelected(null); };

  const handleFormChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.blood_group) {
      toast.error('All fields are required.'); return;
    }
    setSaving(true);
    try {
      if (modal === 'add') {
        await axios.post(`${API}/donors`, form, authHeaders);
        toast.success('Donor added successfully!');
      } else {
        await axios.put(`${API}/donors/${selected.id}`, form, authHeaders);
        toast.success('Donor updated!');
      }
      closeModal();
      fetchDonors();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Operation failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/donors/${selected.id}`, authHeaders);
      toast.success('Donor removed.');
      closeModal();
      fetchDonors();
      fetchStats();
    } catch (err) {
      toast.error('Failed to delete donor.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <>
      <Helmet>
        <title>Admin Dashboard — BloodBridge</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="admin-page">
        {/* Sidebar */}
        <aside className="admin-sidebar">
          <div className="sidebar-brand">
            <Link to="/" className="sb-logo">
              <span className="sb-logo-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M12 2C12 2 5 9.5 5 14a7 7 0 0 0 14 0C19 9.5 12 2 12 2z"/>
                </svg>
              </span>
              <span>Blood<span>Bridge</span></span>
            </Link>
            <span className="sb-badge">Admin</span>
          </div>

          <nav className="sidebar-nav">
            <button className={`sb-nav-item ${activeTab === 'donors' ? 'active' : ''}`} onClick={() => setActiveTab('donors')}>
              <span className="sb-icon">◈</span> Donors
            </button>
            <button className={`sb-nav-item ${activeTab === 'stats' ? 'active' : ''}`} onClick={() => setActiveTab('stats')}>
              <span className="sb-icon">◉</span> Statistics
            </button>
            <button className={`sb-nav-item ${activeTab === 'contacts' ? 'active' : ''}`} onClick={() => setActiveTab('contacts')}>
              <span className="sb-icon">◇</span> Messages
              {contacts.length > 0 && <span className="sb-count">{contacts.length}</span>}
            </button>
          </nav>

          <button className="sb-logout" onClick={logout}>
            <span>↩</span> Logout
          </button>
        </aside>

        {/* Main */}
        <main className="admin-main">
          {/* Header */}
          <header className="admin-header">
            <div>
              <h1 className="admin-title">
                {activeTab === 'donors' && 'Donor Management'}
                {activeTab === 'stats' && 'Statistics'}
                {activeTab === 'contacts' && 'Contact Messages'}
              </h1>
              <p className="admin-subtitle">
                {activeTab === 'donors' && `${total} total donors registered`}
                {activeTab === 'stats' && 'Blood group distribution overview'}
                {activeTab === 'contacts' && `${contacts.length} messages received`}
              </p>
            </div>
            {activeTab === 'donors' && (
              <button className="btn btn-primary" onClick={openAdd}>
                + Add Donor
              </button>
            )}
          </header>

          {/* DONORS TAB */}
          {activeTab === 'donors' && (
            <div className="tab-content">
              {/* Filters */}
              <div className="filters-bar">
                <div className="search-wrap">
                  <span className="search-icon">⌕</span>
                  <input
                    type="text"
                    className="form-input filter-input"
                    placeholder="Search by name, phone, address..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>
                <select
                  className="form-input filter-select"
                  value={filterGroup}
                  onChange={e => setFilterGroup(e.target.value)}
                >
                  <option value="">All Blood Groups</option>
                  {BLOOD_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                {(search || filterGroup) && (
                  <button className="btn-clear" onClick={() => { setSearch(''); setFilterGroup(''); }}>
                    Clear
                  </button>
                )}
              </div>

              {/* Table */}
              {loading ? (
                <div className="loading-wrap"><div className="spinner" /></div>
              ) : donors.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🩸</div>
                  <h3>No donors found</h3>
                  <p>Try a different search or add a new donor.</p>
                  <button className="btn btn-primary" onClick={openAdd}>Add First Donor</button>
                </div>
              ) : (
                <>
                  <div className="table-wrap">
                    <table className="donors-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th>Blood Group</th>
                          <th>Phone</th>
                          <th>Address</th>
                          <th>Registered</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {donors.map((d, i) => (
                          <tr key={d.id}>
                            <td className="td-num">{(page - 1) * 15 + i + 1}</td>
                            <td className="td-name">{d.name}</td>
                            <td>
                              <span className="bg-pill">{d.blood_group}</span>
                            </td>
                            <td><a href={`tel:${d.phone}`} className="td-phone">{d.phone}</a></td>
                            <td className="td-address">{d.address}</td>
                            <td className="td-date">{formatDate(d.created_at)}</td>
                            <td>
                              <div className="action-btns">
                                <button className="action-btn edit-btn" onClick={() => openEdit(d)} title="Edit">✎</button>
                                <button className="action-btn del-btn" onClick={() => openDelete(d)} title="Delete">✕</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button className="page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                      <span className="page-info">Page {page} of {totalPages}</span>
                      <button className="page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* STATS TAB */}
          {activeTab === 'stats' && (
            <div className="tab-content">
              <div className="stats-cards">
                <div className="stat-card stat-card-big">
                  <span className="sc-label">Total Donors</span>
                  <span className="sc-value">{stats.total}</span>
                </div>
                {BLOOD_GROUPS.map(g => {
                  const match = stats.byGroup?.find(b => b.blood_group === g);
                  const count = match?.count || 0;
                  const pct = stats.total ? Math.round((count / stats.total) * 100) : 0;
                  return (
                    <div key={g} className="stat-card">
                      <div className="sc-group-tag">{g}</div>
                      <span className="sc-count">{count}</span>
                      <span className="sc-pct">{pct}%</span>
                      <div className="sc-bar">
                        <div className="sc-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CONTACTS TAB */}
          {activeTab === 'contacts' && (
            <div className="tab-content">
              {contacts.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">✉️</div>
                  <h3>No messages yet</h3>
                  <p>Contact form submissions will appear here.</p>
                </div>
              ) : (
                <div className="contacts-list">
                  {contacts.map((c) => (
                    <div key={c.id} className="contact-msg-card">
                      <div className="cmc-header">
                        <span className="cmc-name">{c.name}</span>
                        <span className="cmc-date">{formatDate(c.created_at)}</span>
                      </div>
                      <div className="cmc-details">
                        <a href={`mailto:${c.email}`}>{c.email}</a>
                        {c.phone && <a href={`tel:${c.phone}`}>{c.phone}</a>}
                      </div>
                      <p className="cmc-message">{c.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* ── MODAL: ADD / EDIT ── */}
      {(modal === 'add' || modal === 'edit') && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modal === 'add' ? 'Add New Donor' : 'Edit Donor'}</h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleSave} className="modal-form">
              <div className="form-group">
                <label className="form-label" htmlFor="m-name">Full Name *</label>
                <input id="m-name" name="name" type="text" className="form-input"
                  placeholder="Donor's full name" value={form.name} onChange={handleFormChange} required />
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label" htmlFor="m-phone">Phone Number *</label>
                  <input id="m-phone" name="phone" type="tel" className="form-input"
                    placeholder="+91 XXXXX XXXXX" value={form.phone} onChange={handleFormChange} required />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="m-bg">Blood Group *</label>
                  <select id="m-bg" name="blood_group" className="form-input" value={form.blood_group} onChange={handleFormChange} required>
                    <option value="">Select Blood Group</option>
                    {BLOOD_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="m-addr">Address *</label>
                <textarea id="m-addr" name="address" className="form-input" rows={3}
                  placeholder="Full address including city, state" value={form.address} onChange={handleFormChange} required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : modal === 'add' ? 'Add Donor' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL: DELETE ── */}
      {modal === 'delete' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Remove Donor</h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to remove <strong>{selected?.name}</strong> ({selected?.blood_group}) from the database?</p>
              <p className="del-warning">This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={closeModal}>Cancel</button>
              <button className="btn btn-delete" onClick={handleDelete} disabled={saving}>
                {saving ? 'Removing...' : 'Yes, Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Admin;
